import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  Card,
  Descriptions,
  Dropdown,
  Empty,
  Modal,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  message,
} from 'antd';
import {
  EditOutlined,
  EyeOutlined,
  DownloadOutlined,
  DownOutlined,
  SendOutlined,
  CopyOutlined,
  FileAddOutlined,
  MoreOutlined,
  PlusOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import PersianDatePicker from '../PersianDatePicker';
import { financeSettingsApi } from '../../utils/api';
import { TableSkeleton } from '../SkeletonLoaders';
import { formatJalaliApiDate, getLast30DaysJalaliApiRange } from '../../utils/dateUtils';
import { formatMoneyFa as formatMoney } from '../../utils/formatPriceToman';
import { parseJalaliDateRangeValue } from '../../utils/filterDateUtils';
import { useListReturnState } from '../../hooks/useListReturnState';
import { formatClientNameWithCompany, resolveClientCompanyName } from '../../utils/clientDisplayUtils';
import { formatItemDiscountLabel } from '../../utils/itemDiscountUtils';

const { Option } = Select;

const statusOptions = [
  { value: 'all', label: 'همه' },
  { value: 'waiting', label: 'در انتظار' },
  { value: 'accepted', label: 'پذیرفته شده' },
  { value: 'declined', label: 'رد شده' },
  { value: 'draft', label: 'پیش‌نویس' },
  { value: 'canceled', label: 'لغو شده' },
];

const statusTag = {
  waiting: { color: 'orange', text: 'در انتظار' },
  accepted: { color: 'green', text: 'پذیرفته شده' },
  declined: { color: 'red', text: 'رد شده' },
  draft: { color: 'blue', text: 'پیش‌نویس' },
  canceled: { color: 'default', text: 'لغو شده' },
  cancelled: { color: 'default', text: 'لغو شده' },
};

const ESTIMATE_STATUS_CHANGE_OPTIONS = [
  { value: 'waiting', label: 'در انتظار', color: 'orange' },
  { value: 'accepted', label: 'پذیرفته شده', color: 'green' },
  { value: 'declined', label: 'رد شده', color: 'red' },
  { value: 'draft', label: 'پیش‌نویس', color: 'blue' },
  { value: 'canceled', label: 'لغو شده', color: 'default' },
];

const InvoiceEstimateTab = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [list, setList] = useState([]);
  const [pagination, setPagination] = useState({ current_page: 1, per_page: 10, total: 0, last_page: 1 });
  const defaultRange = useMemo(() => getLast30DaysJalaliApiRange(), []);
  const [filters, setFilters] = useState({
    startDate: defaultRange.startDate,
    endDate: defaultRange.endDate,
    status: 'all',
    page: 1,
    per_page: 10,
  });
  const [statusLoadingId, setStatusLoadingId] = useState(null);
  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  const captureCurrentListState = useCallback(
    () => ({ filters }),
    [filters],
  );

  const { navigateToDetail } = useListReturnState(captureCurrentListState, (restored) => {
    if (restored.filters) setFilters(restored.filters);
  });

  const [viewModalVisible, setViewModalVisible] = useState(false);
  const [viewData, setViewData] = useState(null);
  const [viewLoading, setViewLoading] = useState(false);
  const dateRangeValue = filters.startDate && filters.endDate ? [filters.startDate, filters.endDate] : null;

  const handleDateRangeChange = (value) => {
    const { startDate, endDate } = parseJalaliDateRangeValue(value);
    setFilters((prev) => ({ ...prev, startDate, endDate, page: 1 }));
  };

  /** همیشه با فیلترهای فعلی (بدون ریست) لیست را می‌گیرد */
  const fetchList = useCallback(async (activeFilters = filtersRef.current) => {
    setLoading(true);
    try {
      const res = await financeSettingsApi.getEstimates(activeFilters);
      setList(res?.estimates || []);
      setPagination(res?.pagination || {
        current_page: activeFilters.page || 1,
        per_page: activeFilters.per_page || 10,
        total: 0,
        last_page: 1,
      });
    } catch (error) {
      console.error('Error fetching estimates:', error);
      message.error('خطا در دریافت پیش‌فاکتورها');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchList(filters);
  }, [filters, fetchList]);

  const changeEstimateStatus = async (record, newStatus) => {
    if (String(record.status) === String(newStatus)) return;
    setStatusLoadingId(record.id);
    try {
      await financeSettingsApi.updateEstimateStatus({
        estimateId: record.id,
        status: newStatus,
      });
      const label = statusTag[newStatus]?.text || newStatus;
      message.success(`وضعیت به «${label}» تغییر کرد`);
      await fetchList(filtersRef.current);
    } catch (error) {
      console.error('خطا در تغییر وضعیت پیش‌فاکتور:', error);
      message.error(error?.message || 'خطا در تغییر وضعیت پیش‌فاکتور');
    } finally {
      setStatusLoadingId(null);
    }
  };

  const openViewModal = async (record) => {
    setViewModalVisible(true);
    setViewLoading(true);
    try {
      const res = await financeSettingsApi.getEstimate(record.id);
      setViewData(res?.data || null);
    } catch (error) {
      message.error('خطا در دریافت جزئیات پیش‌فاکتور');
      setViewModalVisible(false);
    } finally {
      setViewLoading(false);
    }
  };

  const sendEstimate = async (record) => {
    try {
      await financeSettingsApi.sendEstimate(record.id);
      message.success('پیش‌فاکتور ارسال شد');
      await fetchList(filtersRef.current);
    } catch (error) {
      console.error('Send estimate:', error);
      message.error(error?.message || 'خطا در ارسال پیش‌فاکتور');
    }
  };

  const downloadEstimate = async (record) => {
    try {
      await financeSettingsApi.downloadEstimate(record.id);
      message.success('دانلود پیش‌فاکتور آغاز شد');
    } catch (error) {
      console.error('Download estimate:', error);
      message.error(error?.message || 'خطا در دانلود پیش‌فاکتور');
    }
  };

  const columns = [
    {
      title: 'شماره پیش‌فاکتور',
      dataIndex: 'estimate_number',
      key: 'estimate_number',
      render: (v) => <span style={{ fontFamily: 'CustomFont' }}>{v || '-'}</span>,
    },
    {
      title: 'مشتری',
      dataIndex: ['client', 'name_salutation'],
      key: 'client',
      render: (_, r) => (
        <span style={{ fontFamily: 'CustomFont' }}>
          {formatClientNameWithCompany(r.client, r) || '-'}
        </span>
      ),
    },
    {
      title: 'تاریخ اعتبار',
      dataIndex: 'valid_till',
      key: 'valid_till',
      render: (v) => <span style={{ fontFamily: 'CustomFont' }}>{formatJalaliApiDate(v) || '-'}</span>,
    },
    {
      title: 'مبلغ کل',
      dataIndex: 'total',
      key: 'total',
      render: (v, r) => <span style={{ fontFamily: 'CustomFont' }}>{formatMoney(v, r.currency_symbol)}</span>,
    },
    {
      title: 'وضعیت',
      dataIndex: 'status',
      key: 'status',
      width: 180,
      render: (v, record) => {
        const current = String(v || '').toLowerCase() === 'cancelled' ? 'canceled' : v;
        const s = statusTag[current] || statusTag[v] || { color: 'default', text: v || '-' };
        const items = ESTIMATE_STATUS_CHANGE_OPTIONS.map((opt) => ({
          key: opt.value,
          label: (
            <span style={{ fontFamily: 'CustomFont' }}>
              <Tag color={opt.color} style={{ margin: 0, fontFamily: 'CustomFont' }}>{opt.label}</Tag>
              {opt.value === current ? <span className="text-xs text-gray-400 mr-2">✓</span> : null}
            </span>
          ),
        }));
        return (
          <Dropdown
            menu={{ items, onClick: ({ key }) => changeEstimateStatus(record, key) }}
            trigger={['click']}
          >
            <Button
              type="text"
              size="small"
              loading={statusLoadingId === record.id}
              style={{ fontFamily: 'CustomFont' }}
            >
              <Tag color={s.color} style={{ fontFamily: 'CustomFont', marginInlineEnd: 4 }}>{s.text}</Tag>
              <DownOutlined style={{ fontSize: 10 }} />
            </Button>
          </Dropdown>
        );
      },
    },
    {
      title: 'عملیات',
      key: 'actions',
      width: 130,
      render: (_, record) => {
        const menuItems = [
          {
            key: 'view',
            label: 'مشاهده',
            icon: <EyeOutlined />,
            onClick: () => openViewModal(record),
          },
          {
            key: 'download',
            label: 'دانلود',
            icon: <DownloadOutlined />,
            onClick: () => downloadEstimate(record),
          },
          {
            key: 'edit',
            label: 'ویرایش',
            icon: <EditOutlined />,
            onClick: () => navigateToDetail(`/finance/estimates/${record.id}/edit`),
          },
          {
            key: 'send',
            label: 'ارسال',
            icon: <SendOutlined />,
            onClick: () => sendEstimate(record),
          },
          {
            key: 'invoice',
            label: 'ایجاد فاکتور',
            icon: <FileAddOutlined />,
            onClick: () => navigate(`/finance/invoices/add?estimate=${record.id}`),
          },
          {
            key: 'duplicate',
            label: 'ایجاد نسخه مشابه',
            icon: <CopyOutlined />,
            onClick: () => navigate(`/finance/estimates/add?estimate=${record.id}`),
          },
        ];

        return (
          <Dropdown menu={{ items: menuItems }} trigger={['click']}>
            <Button type="text" icon={<MoreOutlined />} style={{ fontFamily: 'CustomFont' }}>
              عملیات
            </Button>
          </Dropdown>
        );
      },
    },
  ];

  const estimateView = viewData?.estimate;
  const estimateClientCompany = resolveClientCompanyName(
    estimateView?.client,
    viewData?.client,
    estimateView?.clientdetails,
    estimateView,
    viewData,
  );
  const estimateClientLabel =
    formatClientNameWithCompany(
      estimateView?.client,
      viewData?.client,
      estimateView?.clientdetails,
      estimateView,
      viewData,
    ) || '-';

  return (
    <div style={{ fontFamily: 'CustomFont' }}>
      <div className="flex flex-wrap gap-2 items-end mb-4">
        <div className="min-w-[260px]">
          <label className="block text-xs mb-1">بازه تاریخ</label>
          <PersianDatePicker
            range
            value={dateRangeValue}
            onChange={handleDateRangeChange}
            placeholder="از تاریخ — تا تاریخ"
            style={{ fontFamily: 'CustomFont', width: '100%' }}
          />
        </div>
        <div>
          <label className="block text-xs mb-1">وضعیت</label>
          <Select value={filters.status} style={{ width: 170 }} onChange={(v) => setFilters((p) => ({ ...p, status: v, page: 1 }))}>
            {statusOptions.map((st) => <Option key={st.value} value={st.value}>{st.label}</Option>)}
          </Select>
        </div>
        <Button icon={<ReloadOutlined />} onClick={() => fetchList(filtersRef.current)} style={{ fontFamily: 'CustomFont' }}>بروزرسانی</Button>
        <Button type="primary" icon={<PlusOutlined />} className="bg-green-600 hover:bg-green-700 border-0" style={{ fontFamily: 'CustomFont' }} onClick={() => navigate('/finance/estimates/add')}>
          افزودن پیش‌فاکتور
        </Button>
      </div>

      {loading ? (
        <TableSkeleton rows={5} columns={4} />
      ) : (
        <Table
          columns={columns}
          dataSource={list}
          rowKey="id"
          pagination={{
            current: pagination.current_page,
            pageSize: pagination.per_page,
            total: pagination.total,
            onChange: (page, perPage) => setFilters((p) => ({ ...p, page, per_page: perPage })),
            showTotal: (t) => <span style={{ fontFamily: 'CustomFont' }}>مجموع: {new Intl.NumberFormat('fa-IR').format(t)} مورد</span>,
          }}
          locale={{
            emptyText: <Empty description={<span style={{ fontFamily: 'CustomFont' }}>پیش‌فاکتوری یافت نشد</span>} />,
          }}
        />
      )}

      <Modal
        open={viewModalVisible}
        onCancel={() => setViewModalVisible(false)}
        title={<span style={{ fontFamily: 'CustomFont' }}>جزئیات پیش‌فاکتور</span>}
        width={900}
        footer={<Button onClick={() => setViewModalVisible(false)} style={{ fontFamily: 'CustomFont' }}>بستن</Button>}
      >
        {viewLoading ? (
          <TableSkeleton rows={4} columns={2} />
        ) : estimateView ? (
          <>
            <Descriptions bordered column={2} className="mb-4" style={{ fontFamily: 'CustomFont' }}>
              <Descriptions.Item label="شماره">{estimateView.estimate_number}</Descriptions.Item>
              <Descriptions.Item label="وضعیت">
                {(() => {
                  const s = statusTag[estimateView.status] || { color: 'default', text: estimateView.status };
                  return <Tag color={s.color}>{s.text}</Tag>;
                })()}
              </Descriptions.Item>
              <Descriptions.Item label="مشتری">{estimateClientLabel}</Descriptions.Item>
              {estimateClientCompany ? (
                <Descriptions.Item label="نام شرکت">{estimateClientCompany}</Descriptions.Item>
              ) : null}
              <Descriptions.Item label="اعتبار تا">{formatJalaliApiDate(estimateView.valid_till) || '-'}</Descriptions.Item>
              <Descriptions.Item label="جمع">{formatMoney(estimateView.sub_total)}</Descriptions.Item>
              <Descriptions.Item label="کل">{formatMoney(estimateView.total, estimateView.currency?.currency_symbol)}</Descriptions.Item>
            </Descriptions>
            <Table
              rowKey="id"
              dataSource={estimateView.items || []}
              pagination={false}
              columns={[
                { title: 'آیتم', dataIndex: 'item_name', render: (t) => <span style={{ fontFamily: 'CustomFont' }}>{t}</span> },
                { title: 'تعداد', dataIndex: 'quantity', render: (v) => <span style={{ fontFamily: 'CustomFont' }}>{v}</span> },
                { title: 'قیمت واحد', dataIndex: 'unit_price', render: (v) => formatMoney(v) },
                {
                  title: 'تخفیف آیتم',
                  key: 'item_discount',
                  render: (_, row) => (
                    <span style={{ fontFamily: 'CustomFont' }}>
                      {formatItemDiscountLabel(row, formatMoney)}
                    </span>
                  ),
                },
                { title: 'مبلغ', dataIndex: 'amount', render: (v) => formatMoney(v) },
              ]}
            />
          </>
        ) : (
          <Empty />
        )}
      </Modal>
    </div>
  );
};

export default InvoiceEstimateTab;
